/**
 * Edits to the RERA import draft, kept apart from the modal so they can be
 * tested. The draft is blocks -> levels (floors) -> units, plus the list of
 * accommodation types the units point at. Every helper returns a new draft.
 */

const mapAt = (list, index, fn) => list.map((item, i) => (i === index ? fn(item) : item));

/** Drop empty floors and blocks, and accommodation types no unit uses any more. */
export const pruneDraft = (draft) => {
  const blocks = draft.blocks
    .map((b) => ({ ...b, levels: b.levels.filter((l) => l.units.length > 0) }))
    .filter((b) => b.levels.length > 0);
  const used = new Set(blocks.flatMap((b) => b.levels.flatMap((l) => l.units.map((u) => u.accommodationKey))));
  return { ...draft, blocks, accommodations: draft.accommodations.filter((a) => used.has(a.key)) };
};

export const countDraft = (draft) => ({
  accommodations: draft.accommodations.length,
  blocks: draft.blocks.length,
  levels: draft.blocks.reduce((n, b) => n + b.levels.length, 0),
  units: draft.blocks.reduce((n, b) => n + b.levels.reduce((m, l) => m + l.units.length, 0), 0)
});

export const renameAccommodation = (draft, key, name) => ({
  ...draft,
  accommodations: draft.accommodations.map((a) => (a.key === key ? { ...a, name } : a))
});

/** Set a plot type's width or depth. A blank value clears it; a plot needs both to stay regular. */
export const setAccommodationSize = (draft, key, field, value) => {
  const parsed = value === '' || value == null ? null : Number(value);
  const size = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  return {
    ...draft,
    accommodations: draft.accommodations.map((a) => {
      if (a.key !== key) return a;
      const next = { ...a, [field]: size };
      return { ...next, plotType: next.width && next.length ? 'regular' : 'irregular' };
    })
  };
};

export const renameBlock = (draft, bi, name) => ({
  ...draft,
  blocks: mapAt(draft.blocks, bi, (b) => ({ ...b, name }))
});

export const renameLevel = (draft, bi, li, name) => ({
  ...draft,
  blocks: mapAt(draft.blocks, bi, (b) => ({ ...b, levels: mapAt(b.levels, li, (l) => ({ ...l, name })) }))
});

export const renameUnit = (draft, bi, li, ui, name) => ({
  ...draft,
  blocks: mapAt(draft.blocks, bi, (b) => ({
    ...b,
    levels: mapAt(b.levels, li, (l) => ({ ...l, units: mapAt(l.units, ui, (u) => ({ ...u, unit_name: name })) }))
  }))
});

export const removeUnit = (draft, bi, li, ui) =>
  pruneDraft({
    ...draft,
    blocks: mapAt(draft.blocks, bi, (b) => ({
      ...b,
      levels: mapAt(b.levels, li, (l) => ({ ...l, units: l.units.filter((_, i) => i !== ui) }))
    }))
  });

export const removeLevel = (draft, bi, li) =>
  pruneDraft({
    ...draft,
    blocks: mapAt(draft.blocks, bi, (b) => ({ ...b, levels: b.levels.filter((_, i) => i !== li) }))
  });

export const removeBlock = (draft, bi) =>
  pruneDraft({ ...draft, blocks: draft.blocks.filter((_, i) => i !== bi) });

/** Problems that would make the server refuse the draft, found before the user submits. */
export const findDraftProblems = (draft) => {
  const problems = [];
  if (!draft.blocks.length) return ['Nothing to import'];
  draft.accommodations.forEach((a) => {
    if (!a.name.trim()) problems.push('An accommodation has no name');
  });
  const blockNames = new Set();
  draft.blocks.forEach((b) => {
    const bn = b.name.trim().toLowerCase();
    if (!bn) problems.push('A block has no name');
    else if (blockNames.has(bn)) problems.push(`Two blocks are named "${b.name.trim()}"`);
    blockNames.add(bn);
    const levelNames = new Set();
    const unitNames = new Set();
    b.levels.forEach((l) => {
      const ln = l.name.trim().toLowerCase();
      if (!ln) problems.push(`A level in ${b.name} has no name`);
      else if (levelNames.has(ln)) problems.push(`${b.name} has two levels named "${l.name.trim()}"`);
      levelNames.add(ln);
      l.units.forEach((u) => {
        const un = u.unit_name.trim().toLowerCase();
        if (!un) problems.push(`A unit in ${b.name} has no name`);
        else if (unitNames.has(un)) problems.push(`${b.name} has two units named "${u.unit_name.trim()}"`);
        unitNames.add(un);
      });
    });
  });
  return [...new Set(problems)];
};
